export function normalizePosts(items = []) {
    return items.map((item) => ({
        ...item,
        post: item.post ?? item.postCode ?? "",
        twoDigit: item.twoDigit ?? item.twoD ?? "",
        threeDigit: item.threeDigit ?? item.threeD ?? "",
        fourDigit: item.fourDigit ?? item.fourD ?? "",
        fiveDigit: item.fiveDigit ?? item.fiveD ?? "",
    }));
}

const normalizeLegacyLOItem = (item = {}) => ({
    ...item,
    post: item.post ?? item.postCode ?? "",
    twoDigit: item.twoDigit ?? item.twoD ?? "",
    threeDigit: item.threeDigit ?? item.threeD ?? "",
});

const normalizeLOBucketItem = (item = {}, valueKey) => ({
    ...item,
    post: item.post ?? item.postCode ?? "",
    columnNumber: item.columnNumber ?? "",
    [valueKey]:
        item.number ??
        item[valueKey] ??
        (valueKey === "twoDigit" ? item.twoD : item.threeD) ??
        "",
});

// Backend now supports two LO shapes:
// 1. legacy array: LO: [{ post: "LO1", twoDigit, threeDigit, ... }]
// 2. split buckets: LO: { "2D": [{ post, number }], "3D": [{ post, number }] }
// We merge the bucketed shape back into the same renderer-friendly item model.
const mergeLOBuckets = (loBuckets = {}) => {
    const { twoD: bucket2d, threeD: bucket3d } = normalizeLOValueBuckets(loBuckets);
    const itemMap = new Map();

    const ensureItem = (item = {}) => {
        const post = item.post ?? item.postCode ?? "";
        if (!post) return null;
        const columnNumber = item.columnNumber ?? "";
        const itemKey = `${post}::${columnNumber}`;

        if (!itemMap.has(itemKey)) {
            itemMap.set(itemKey, {
                ...item,
                post,
                columnNumber,
                twoDigit: "",
                threeDigit: "",
            });
        }

        return itemMap.get(itemKey);
    };

    bucket2d.forEach((item) => {
        const target = ensureItem(item);
        if (!target) return;

        target.twoDigit = item.twoDigit ?? "";
    });

    bucket3d.forEach((item) => {
        const target = ensureItem(item);
        if (!target) return;

        target.threeDigit = item.threeDigit ?? "";
    });

    return Array.from(itemMap.values());
};

export function normalizeLO(items = []) {
    return Array.isArray(items)
        ? items.map(normalizeLegacyLOItem)
        : mergeLOBuckets(items);
}

export function normalizeLOColumnPairs(items = []) {
    if (Array.isArray(items)) {
        return items.reduce((acc, item) => {
            const normalizedItem = normalizeLegacyLOItem(item);
            const columnNumber = String(item.columnNumber ?? "");

            if (!columnNumber) return acc;

            if (!acc[columnNumber]) {
                acc[columnNumber] = [];
            }

            acc[columnNumber].push({
                post: normalizedItem.post,
                columnNumber: item.columnNumber ?? "",
                twoDigit: normalizedItem.twoDigit ?? "",
                threeDigit: normalizedItem.threeDigit ?? "",
            });

            return acc;
        }, {});
    }

    const { twoD, threeD } = normalizeLOValueBuckets(items);
    const grouped2D = twoD.reduce((acc, item) => {
        const columnNumber = String(item.columnNumber ?? "");
        if (!columnNumber) return acc;
        if (!acc[columnNumber]) {
            acc[columnNumber] = [];
        }
        acc[columnNumber].push(item);
        return acc;
    }, {});
    const grouped3D = threeD.reduce((acc, item) => {
        const columnNumber = String(item.columnNumber ?? "");
        if (!columnNumber) return acc;
        if (!acc[columnNumber]) {
            acc[columnNumber] = [];
        }
        acc[columnNumber].push(item);
        return acc;
    }, {});

    const allColumnNumbers = Array.from(
        new Set([...Object.keys(grouped2D), ...Object.keys(grouped3D)])
    );

    return allColumnNumbers.reduce((acc, columnNumber) => {
        const items2D = grouped2D[columnNumber] || [];
        const items3D = grouped3D[columnNumber] || [];
        const pairCount = Math.max(items2D.length, items3D.length);

        acc[columnNumber] = Array.from({ length: pairCount }, (_, index) => {
            const item2D = items2D[index];
            const item3D = items3D[index];

            return {
                post: item2D?.post ?? item3D?.post ?? "",
                columnNumber: item2D?.columnNumber ?? item3D?.columnNumber ?? "",
                twoDigit: item2D?.twoDigit ?? "",
                threeDigit: item3D?.threeDigit ?? "",
            };
        });

        return acc;
    }, {});
}

export function normalizeLOValueBuckets(items = []) {
    if (Array.isArray(items)) {
        const normalized = items.map(normalizeLegacyLOItem);

        return {
            twoD: normalized.map((item) => ({
                ...item,
                twoDigit: item.twoDigit ?? "",
            })),
            threeD: normalized.map((item) => ({
                ...item,
                threeDigit: item.threeDigit ?? "",
            })),
        };
    }

    return {
        twoD: Array.isArray(items?.["2D"])
            ? items["2D"].map((item) => normalizeLOBucketItem(item, "twoDigit"))
            : [],
        threeD: Array.isArray(items?.["3D"])
            ? items["3D"].map((item) => normalizeLOBucketItem(item, "threeDigit"))
            : [],
    };
}
